import {FC} from 'react'
import {useForm, Controller, FormProvider} from 'react-hook-form'
import {useSelector} from 'react-redux'
import {useHistory} from 'react-router-dom'

import {Textarea} from 'jbc-front/components/presenters/form/Textarea'
import {Label, Required} from 'jbc-front/components/presenters/form/Label'
import {Input} from 'jbc-front/components/presenters/form/Input'
import {Spacer} from 'jbc-front/components/presenters/Spacer'
import {Checkbox} from 'jbc-front/components/presenters/form/Checkbox'
import {ErrorMessage} from 'jbc-front/components/presenters/form/ErrorMessage'
import {Select, Option} from 'jbc-front/components/presenters/form/Select'
import {Radio, RadioContainer} from 'jbc-front/components/presenters/form/Radio'
import Button from 'jbc-front/components/Button'
import {Section} from 'jbc-front/components/Form'

import {yupResolver} from '@hookform/resolvers/yup'
import {CustomEmployeeFieldGroup, convertFieldsToCustomValues} from '../query'
import {AdminMailBody} from './AdminMailBody'
import {EmployeeMailBody} from './EmployeeMailBody'
import {OfficeCheckboxField} from './OfficeCheckboxField'
import {schema, Schema} from '../schema'
import {FormField} from 'components/forms/FormField'
import {FormSection} from 'components/forms/FormSection'
import {
  wrapperSectionStyle,
  basicBaseDateTypeSelectOptions,
  reasonTypeSelectOptions,
  alertTargetSelectOptions,
  colors,
  toColor,
  dateUnitOptionsFilter,
} from './formUtils'
import {BASIC_BASE_DATE_TYPE_VALUES} from 'consts/alertCustom'

import styles from './AlertTypeCreateForm.scss'
import {isOfficeAdminSelector} from 'libs/redux'

type AlertTypeCreateFormProps = {
  onSubmit: (data: Schema) => Promise<void>
  customFields: CustomEmployeeFieldGroup[]
  loading: boolean
}

export const AlertTypeCreateForm: FC<AlertTypeCreateFormProps> = ({onSubmit, loading, customFields}) => {
  const history = useHistory()
  const baseDateTypeSelectOptions: Option[] = [
    ...basicBaseDateTypeSelectOptions,
    ...convertFieldsToCustomValues(customFields),
  ]

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      description: '',
      baseDateType: baseDateTypeSelectOptions[0],
      alertTargets: [alertTargetSelectOptions[0]],
      reasonType: reasonTypeSelectOptions[0],
      dateUnit: 'day',
      color: 0x3498db,
      active: true,
      selectedAllOffices: true,
      selectedOfficeIds: [],
    },
  })
  const {
    register,
    handleSubmit,
    watch,
    control,
    trigger,
    formState: {errors, isValid},
  } = methods

  const selectedBaseDateType = watch('baseDateType')
  const adminMailActive = watch('adminMailActive')
  const employeeMailActive = watch('employeeMailActive')

  const isAgeOrBirthday = ['age', 'birthday'].includes(selectedBaseDateType?.value)

  const filteredDateUnitOptions = dateUnitOptionsFilter(selectedBaseDateType.value)

  const isOfficeAdmin = useSelector(isOfficeAdminSelector)

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)} className={styles.FormAlertCustomization}>
        <Section title="アラート設定" style={wrapperSectionStyle}>
          <FormSection>
            <FormField>
              <Label>アラートカラー</Label>
              <Spacer direction="y" size={8} />
              <RadioContainer className={styles.radioColorContainer}>
                <Controller
                  name="color"
                  control={control}
                  render={({field}) => (
                    <>
                      {colors.map((color) => (
                        <Radio
                          key={color}
                          value={color}
                          checked={field.value === color}
                          onChange={() => field.onChange(color)}
                        >
                          <span className={styles.colorOption} style={{backgroundColor: toColor(color)}}></span>
                        </Radio>
                      ))}
                    </>
                  )}
                />
              </RadioContainer>
            </FormField>
            <FormField>
              <FormField.LabelContainer>
                <Label>
                  アラート名
                  <Spacer direction="x" size={8} />
                  <Required />
                </Label>
              </FormField.LabelContainer>
              <Input {...register('name')} />
              <FormField.ErrorContainer>
                {errors.name?.message && <ErrorMessage>{errors.name.message}</ErrorMessage>}
              </FormField.ErrorContainer>
            </FormField>

            <FormField>
              <FormField.LabelContainer>
                <Label>説明</Label>
              </FormField.LabelContainer>
              <Textarea rows={4} {...register('description')} />
              <FormField.ErrorContainer>
                {errors.description?.message && <ErrorMessage>{errors.description.message}</ErrorMessage>}
              </FormField.ErrorContainer>
            </FormField>

            <Section title="通知基準" style={wrapperSectionStyle}>
              <div className={styles.baseDateContainer}>
                <div>
                  <FormField.LabelContainer>
                    <Label>
                      基準日
                      <Spacer direction="x" size={8} />
                      <Required />
                    </Label>
                  </FormField.LabelContainer>
                  <div className={styles.baseDateArea}>
                    <div className={styles.baseDateSelect}>
                      <Controller
                        name="baseDateType"
                        control={control}
                        render={({field}) => {
                          const selectedOption = baseDateTypeSelectOptions.find(
                            (option) => option.value === field.value?.value
                          )
                          return (
                            <Select
                              options={baseDateTypeSelectOptions}
                              value={selectedOption}
                              onChange={(val: Option) => {
                                field.onChange(val)
                                trigger('offset')
                                trigger('days')
                                trigger('dateUnit')
                                trigger('alertTargets')
                              }}
                              noOptionsMessage={() => 'データが見つかりません'}
                            />
                          )
                        }}
                      />
                    </div>
                    {['age', 'service_years'].includes(selectedBaseDateType.value) && (
                      <div>
                        <Input {...register('offset')} />
                        <span>
                          {selectedBaseDateType.value === 'age'
                            ? '歳'
                            : selectedBaseDateType.value === 'service_years'
                              ? '年'
                              : ''}
                        </span>
                      </div>
                    )}

                    {['absence_start_day', 'absence_end_day'].includes(selectedBaseDateType.value) && (
                      <div className={styles.reasonTypeSelect}>
                        <Controller
                          name="reasonType"
                          control={control}
                          render={({field}) => (
                            <Select
                              options={reasonTypeSelectOptions}
                              value={field.value}
                              onChange={(val: Option) => {
                                field.onChange(val)
                              }}
                              noOptionsMessage={() => 'データが見つかりません'}
                            />
                          )}
                        />
                      </div>
                    )}

                    <div>
                      <Input className={styles.inputDays} {...register('days')} />
                      <div className={styles.dateUnitField}>
                        <Controller
                          name="dateUnit"
                          control={control}
                          render={({field}) => (
                            <Select
                              options={filteredDateUnitOptions}
                              value={filteredDateUnitOptions?.find((option: Option) => option.value === field.value)}
                              onChange={(val: Option) => {
                                field.onChange(val.value)
                                trigger('days')
                              }}
                              noOptionsMessage={() => 'データが見つかりません'}
                            />
                          )}
                        />
                      </div>

                      <RadioContainer className={styles.radioContainer}>
                        <Radio {...register('isBefore')} value="Before" defaultChecked={true}>
                          前
                        </Radio>
                        <Radio {...register('isBefore')} value="After">
                          後
                        </Radio>
                      </RadioContainer>
                    </div>
                  </div>
                </div>
                <div>
                  <FormField.LabelContainer>
                    <Label>
                      対象者
                      <Spacer direction="x" size={8} />
                      {isAgeOrBirthday && <Required />}
                    </Label>
                  </FormField.LabelContainer>
                  <div className={styles.alertTargetArea}>
                    <div className={styles.alertTargetCheckBoxList}>
                      <Controller
                        name="alertTargets"
                        control={control}
                        render={({field}) => {
                          const selectedBaseDateType = watch('baseDateType')
                          const isCustomBaseDateType = !BASIC_BASE_DATE_TYPE_VALUES.includes(
                            selectedBaseDateType?.value
                          )

                          const value: Option[] = isAgeOrBirthday
                            ? field.value
                            : isCustomBaseDateType
                              ? []
                              : [alertTargetSelectOptions[0]!]

                          const handleCheckAlertTarget = (option: Option, checked: boolean) => {
                            if (!isAgeOrBirthday) return

                            let newValue = field.value || []
                            if (checked) {
                              newValue = [...newValue, option]
                            } else {
                              newValue = newValue.filter((v: Option) => v.value !== option.value)
                            }
                            field.onChange(newValue)
                          }

                          return (
                            <div className={styles.alertTargetCheckBoxList}>
                              {alertTargetSelectOptions.map((option) => {
                                const checked = value?.some((v: Option) => v.value === option.value)

                                return (
                                  <div key={option.value}>
                                    <Checkbox
                                      disabled={!isAgeOrBirthday}
                                      checked={checked}
                                      onChange={(e) => handleCheckAlertTarget(option, e.target.checked)}
                                    >
                                      {option.label}
                                    </Checkbox>
                                  </div>
                                )
                              })}
                            </div>
                          )
                        }}
                      />
                    </div>
                  </div>
                </div>
                {errors.alertTargets?.message && <ErrorMessage>{errors.alertTargets.message}</ErrorMessage>}
                {errors.offset?.message && <ErrorMessage>{errors.offset.message}</ErrorMessage>}
                {errors.days?.message && <ErrorMessage>{errors.days.message}</ErrorMessage>}
                {errors.dateUnit?.message && <ErrorMessage>{errors.dateUnit.message}</ErrorMessage>}
              </div>
            </Section>

            <Section title="通知先" style={wrapperSectionStyle}>
              <div className={styles.checkBoxList}>
                <Checkbox {...register('adminMailActive')}>管理者にメール通知</Checkbox>
                <Checkbox {...register('employeeMailActive')}>従業員にメール通知</Checkbox>
              </div>
            </Section>

            <Section title="適用範囲" style={wrapperSectionStyle}>
              <div className={styles.baseOfficeContainer}>
                <FormField>
                  <FormField.LabelContainer>
                    <Label>
                      適用範囲
                      <Spacer direction="x" size={8} />
                      <Required />
                    </Label>
                  </FormField.LabelContainer>
                  <RadioContainer>
                    <Controller
                      name="selectedAllOffices"
                      control={control}
                      render={({field}) => (
                        <>
                          <Radio value="true" checked={field.value === true} onChange={() => field.onChange(true)}>
                            {isOfficeAdmin ? '全事業所' : '全社'}
                          </Radio>
                          <Radio
                            value="false"
                            checked={field.value === false}
                            onChange={() => {
                              field.onChange(false)
                              trigger('selectedOfficeIds')
                            }}
                          >
                            適用事業所を選ぶ
                          </Radio>
                        </>
                      )}
                    />
                  </RadioContainer>
                </FormField>
                {!watch('selectedAllOffices') && (
                  <FormProvider {...methods}>
                    <OfficeCheckboxField
                      name="selectedOfficeIds"
                      label="適用事業所"
                      filterByCurrentOffices={false}
                      required
                      error={errors.selectedOfficeIds?.message}
                    />
                  </FormProvider>
                )}
              </div>
            </Section>

            <Checkbox {...register('active')}>このアラート設定を有効にする</Checkbox>
          </FormSection>
        </Section>

        {adminMailActive && (
          <Section title="メール設定（管理者）" style={wrapperSectionStyle}>
            <FormSection>
              <FormField>
                <FormField.LabelContainer>
                  <Label>
                    件名
                    <Spacer direction="x" size={8} />
                    <Required />
                  </Label>
                </FormField.LabelContainer>
                <Input {...register('adminMailSubject')} />
                <FormField.ErrorContainer>
                  {errors.adminMailSubject?.message && <ErrorMessage>{errors.adminMailSubject.message}</ErrorMessage>}
                </FormField.ErrorContainer>
              </FormField>

              <FormField>
                <FormField.LabelContainer>
                  <Label>
                    本文
                    <Spacer direction="x" size={8} />
                    <Required />
                  </Label>
                </FormField.LabelContainer>
                <FormField.ErrorContainer>
                  {errors.adminMailBody?.message && <ErrorMessage>{errors.adminMailBody.message}</ErrorMessage>}
                </FormField.ErrorContainer>
                <AdminMailBody name="adminMailBody" control={control} />
              </FormField>
            </FormSection>
          </Section>
        )}

        {employeeMailActive && (
          <Section title="メール設定（従業員）" style={wrapperSectionStyle}>
            <FormSection>
              <FormField>
                <FormField.LabelContainer>
                  <Label>
                    件名
                    <Spacer direction="x" size={8} />
                    <Required />
                  </Label>
                </FormField.LabelContainer>
                <Input {...register('employeeMailSubject')} />
                <FormField.ErrorContainer>
                  {errors.employeeMailSubject?.message && (
                    <ErrorMessage>{errors.employeeMailSubject.message}</ErrorMessage>
                  )}
                </FormField.ErrorContainer>
              </FormField>

              <FormField>
                <FormField.LabelContainer>
                  <Label>
                    本文
                    <Spacer direction="x" size={8} />
                    <Required />
                  </Label>
                </FormField.LabelContainer>
                <FormField.ErrorContainer>
                  {errors.employeeMailBody?.message && <ErrorMessage>{errors.employeeMailBody.message}</ErrorMessage>}
                </FormField.ErrorContainer>
                <EmployeeMailBody name="employeeMailBody" control={control} />
              </FormField>
            </FormSection>
          </Section>
        )}

        <div className={styles.bottomButtonsArea}>
          <Button onClick={() => history.push('/settings/alert_custom')} disabled={loading}>
            キャンセル
          </Button>
          <Button onClick={handleSubmit(onSubmit)} primary disabled={loading || !isValid}>
            保存
          </Button>
        </div>
      </form>
    </div>
  )
}
